import { useCallback, useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { apiCall } from "../../lib/api-client.js";
import { authAtom } from "../../lib/auth-store.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import { onboardingPath } from "../../lib/api-paths.js";
import { ApiError, type OrgOnboarding } from "../../lib/api-types.js";
import { InlineLoading } from "../../components/Spinner.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { Stepper } from "../../components/Stepper.js";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../components/FormField.js";
import { cn } from "../../lib/cn.js";
import type { OrgDetailContext } from "../OrganizationDetail/context.js";
import { ONBOARDING_STEPS } from "./types.js";
import { Step1Feishu } from "./steps/Step1Feishu.js";
import { Step2DeptTree } from "./steps/Step2DeptTree.js";
import { Step3Roles } from "./steps/Step3Roles.js";
import { Step4Mapping } from "./steps/Step4Mapping.js";
import { Step5Admin } from "./steps/Step5Admin.js";

const TOTAL = ONBOARDING_STEPS.length;

export function OrganizationOnboarding(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, error, refetch } = useApiQuery<OrgOnboarding>(
    onboardingPath(org.code),
    [org.code],
  );

  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [ready, setReady] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [navError, setNavError] = useState<string | null>(null);

  const completedSteps = useMemo(
    () => data?.completedSteps ?? [],
    [data?.completedSteps],
  );
  const allComplete = Boolean(data?.completedAt);

  // 初始 activeStep：?step= 优先，否则 onboarding.currentStep
  useEffect(() => {
    if (data == null || activeStep != null) return;
    const fromQuery = Number(searchParams.get("step"));
    const initial =
      Number.isInteger(fromQuery) && fromQuery >= 1 && fromQuery <= TOTAL
        ? fromQuery
        : data.currentStep;
    setActiveStep(initial);
  }, [data, activeStep, searchParams]);

  // 切步时重置就绪态：已完成的步骤默认即就绪
  useEffect(() => {
    if (activeStep == null) return;
    setReady(completedSteps.includes(activeStep));
    setNavError(null);
  }, [activeStep, completedSteps]);

  const goToStep = useCallback(
    (step: number) => {
      setActiveStep(step);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("step", String(step));
          return next;
        },
        { replace: true },
      );
      if (auth) {
        void apiCall("PATCH", onboardingPath(org.code), {
          token: auth.token,
          body: { currentStep: step },
        }).catch(() => {
          /* 进度记录 best-effort，不阻塞导航 */
        });
      }
    },
    [auth, org.code, setSearchParams],
  );

  const handleNext = useCallback(async () => {
    if (activeStep == null || !auth) return;
    setAdvancing(true);
    setNavError(null);
    try {
      await apiCall<OrgOnboarding>("PATCH", onboardingPath(org.code), {
        token: auth.token,
        body: { completeStep: activeStep },
      });
      await refetch();
      if (activeStep < TOTAL) {
        goToStep(activeStep + 1);
      }
    } catch (err) {
      setNavError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setAdvancing(false);
    }
  }, [activeStep, auth, org.code, refetch, goToStep]);

  if (loading && data == null) return <InlineLoading label="加载搭建进度…" />;
  if (error) return <ErrorBanner>{error.message}</ErrorBanner>;
  if (data == null || activeStep == null)
    return <InlineLoading label="加载搭建进度…" />;

  const meta = ONBOARDING_STEPS[activeStep - 1];
  const isLast = activeStep === TOTAL;
  const StepBody = renderStep(activeStep, {
    org,
    alreadyCompleted: completedSteps.includes(activeStep),
    onReadyChange: setReady,
  });

  return (
    <div className="flex h-full flex-col">
      {/* 顶部引导 */}
      <header className="mb-5 shrink-0">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-dosia-text">组织搭建</h2>
          <span className="text-xs text-dosia-text-dim">
            第 {activeStep} / {TOTAL} 步
          </span>
        </div>
        <p className="mt-1 text-sm text-dosia-text-secondary">
          跟着 {TOTAL} 步，把贵公司的飞书组织接入 DOSIA。每步做完点「下一步」即可，随时可回头修改。
        </p>
        {allComplete ? (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-dosia-accent/40 bg-dosia-accent/10 px-3 py-2 text-sm text-dosia-text">
            <CheckBadge />
            <span>组织底座已搭建完成。你仍可回到任意步骤调整配置。</span>
          </div>
        ) : null}
      </header>

      {/* 主体：左 stepper + 右内容 */}
      <div className="flex min-h-0 flex-1 gap-8">
        <aside className="w-56 shrink-0 overflow-y-auto pr-1">
          <Stepper
            items={ONBOARDING_STEPS.map((s) => ({
              step: s.step,
              title: s.navTitle,
              subtitle: s.navSubtitle,
            }))}
            activeStep={activeStep}
            completedSteps={completedSteps}
            onSelect={goToStep}
          />
        </aside>

        <section className="flex min-w-0 flex-1 flex-col">
          <div className="mb-4 shrink-0">
            <h3 className="text-base font-semibold text-dosia-text">
              {meta.heading}
            </h3>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-dosia-text-secondary">
              {meta.intro}
            </p>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto pb-4">{StepBody}</div>

          {/* 底部导航 */}
          <footer className="mt-3 shrink-0 border-t border-dosia-border pt-3">
            {navError ? (
              <p className="mb-2 text-xs text-amber-700">{navError}</p>
            ) : null}
            {!ready ? (
              <p className="mb-2 text-xs text-dosia-text-dim">
                {meta.blockedHint}
              </p>
            ) : null}
            <div className="flex items-center justify-between">
              <SecondaryButton
                onClick={() => goToStep(Math.max(1, activeStep - 1))}
                disabled={activeStep === 1 || advancing}
              >
                上一步
              </SecondaryButton>
              <PrimaryButton
                onClick={() => void handleNext()}
                disabled={!ready || advancing}
                className={cn(isLast && "px-5")}
              >
                {advancing
                  ? "处理中…"
                  : isLast
                    ? "完成搭建"
                    : "下一步"}
              </PrimaryButton>
            </div>
          </footer>
        </section>
      </div>
    </div>
  );
}

function renderStep(
  step: number,
  props: {
    org: OrgDetailContext["org"];
    alreadyCompleted: boolean;
    onReadyChange: (ready: boolean) => void;
  },
): JSX.Element {
  switch (step) {
    case 1:
      return <Step1Feishu {...props} />;
    case 2:
      return <Step2DeptTree {...props} />;
    case 3:
      return <Step3Roles {...props} />;
    case 4:
      return <Step4Mapping {...props} />;
    case 5:
      return <Step5Admin {...props} />;
    default:
      return <></>;
  }
}

function CheckBadge(): JSX.Element {
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-dosia-accent text-dosia-bg">
      <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" aria-hidden>
        <path
          d="M3.5 8.5l3 3 6-6.5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
